import React, { useState, useEffect } from 'react';
import { apiEndpoints } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import CostForm from '../components/CostForm';
import EventForm from '../components/EventForm';
import AccidentForm from '../components/AccidentForm';
import DocViewer from '../components/DocViewer';
import { COST_TYPE_COLORS as TYPE_COLORS } from '../utils/costTypes';

const DAMAGE_COLORS = { minor: '#f59e0b', major: '#ef4444', total_loss: '#7c3aed' };
const DAMAGE_LABELS = { minor: 'Minor', major: 'Major', total_loss: 'Total Loss' };
const CLAIM_COLORS  = { none: '#9ca3af', open: '#3b82f6', closed: '#10b981', rejected: '#ef4444' };

const getFileUrl = (filename) => {
  if (!filename) return null;
  const base = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/api\/?$/, '');
  return `${base}/uploads/costs/${filename}`;
};

const formatDate = (v) => v ? new Date(v).toLocaleDateString() : '—';

const getStatus = (event) => {
  const st = (event.status || '').toLowerCase();
  if (st.includes('delay') || st.includes('late') || st.includes('pending')) return 'critical';
  if (st.includes('complete') || st.includes('done') || st.includes('finished')) return 'good';
  return 'active';
};
const STATUS_LABEL = { good: 'COMPLETED', critical: 'DELAY', active: 'ACTIVE' };
const STATUS_COLOR = { good: '#10b981', critical: '#ef4444', active: '#3b82f6' };
const STATUS_BG    = { good: '#ecfdf5',  critical: '#fef2f2',  active: '#eff6ff' };

const DriverCosts = () => {
  const { user } = useAppContext();
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);

  const [previewDoc, setPreviewDoc] = useState(null);

  // detail-view related data
  const [relatedEvents, setRelatedEvents] = useState([]);
  const [relatedAccidents, setRelatedAccidents] = useState([]);
  const [relatedVehicle, setRelatedVehicle] = useState(null);
  const [showEventForm, setShowEventForm] = useState(false);
  const [showAccidentForm, setShowAccidentForm] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await apiEndpoints.costs.list();
      const id = user?.driverId || '';
      const mine = id ? (res.data || []).filter(c => c.driverId === id) : [];
      setCosts(mine);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  useEffect(() => {
    if (!selected?.plate) { setRelatedEvents([]); setRelatedAccidents([]); setRelatedVehicle(null); return; }
    const plate = selected.plate;
    Promise.all([apiEndpoints.events.list(), apiEndpoints.accidents.list(), apiEndpoints.vehicles.list()])
      .then(([evtRes, accRes, vehiclesRes]) => {
        setRelatedEvents((evtRes.data || []).filter(e => e.plate === plate));
        setRelatedAccidents((accRes.data || []).filter(a => a.plate === plate));
        setRelatedVehicle((vehiclesRes.data || []).find(v => v.plate === plate) || null);
      })
      .catch(() => {});
  }, [selected?.plate]);

  const handleSave = async (data, file) => {
    try { await apiEndpoints.costs.create({ ...data, driverId: user.driverId || '' }, file); setShowForm(false); await load(); }
    catch { alert('Error saving cost. Please try again.'); }
  };

  const handleSaveEvent = async (payload) => {
    try { await apiEndpoints.events.create({ ...payload, resp: user.name }); setShowEventForm(false); }
    catch { alert('Error saving event. Please try again.'); }
  };

  const handleSaveAccident = async (data, file) => {
    try { await apiEndpoints.accidents.create({ ...data, driverName: user.name, driverId: user.driverId || '' }, file); setShowAccidentForm(false); }
    catch { alert('Error saving accident. Please try again.'); }
  };

  const total = costs.reduce((s, c) => s + (c.amount || 0), 0);
  const types = [...new Set(costs.map(c => c.costType).filter(Boolean))];
  const byType = types.reduce((acc, t) => { acc[t] = costs.filter(c => c.costType === t).reduce((s, c) => s + (c.amount || 0), 0); return acc; }, {});

  if (loading) return <div className="page-loading">Loading costs…</div>;

  // ── DETAIL VIEW ──────────────────────────────────────────────────────────────
  if (selected) {
    const color = TYPE_COLORS[selected.costType] || '#9ca3af';
    const plate = selected.plate || '';

    return (
      <div>
        <button className="back-btn" onClick={() => setSelected(null)}>
          <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" /></svg>
          Back to costs
        </button>

        <div className="vprofile-header">
          <div className="vprofile-plate"><span className="plate-pill">{plate || '—'}</span></div>
          <div className="vprofile-model">{selected.costType} — {selected.amount?.toLocaleString()} {selected.currency || 'RON'}</div>
          <div className="vprofile-sub">{selected.vehicleName || ''}{selected.company ? ` • ${selected.company}` : ''}</div>
        </div>

        <div className="detail-grid">
          {/* Cost Details */}
          <div className="card">
            <div className="card-header"><div className="card-title">Cost Details</div></div>
            <div className="card-body">
              {[['Plate', plate || '—'], ['Vehicle', selected.vehicleName || '—'], ['Company', selected.company || '—'], ['Date', formatDate(selected.date)], ['Type', selected.costType || '—'], ['Amount', selected.amount ? `${selected.amount.toLocaleString()} ${selected.currency || 'RON'}` : '—'], ['Receipt / Ref', selected.receiptRef || '—'], ['Description', selected.description || '—']].map(([lbl, val]) => (
                <div key={lbl} className="info-row"><div className="info-lbl">{lbl}</div><div className="info-val">{val}</div></div>
              ))}
              <div className="info-row" style={{ borderBottom: 'none' }}>
                <div className="info-lbl">Attachment</div>
                <div className="info-val">
                  {getFileUrl(selected.attachment?.filename)
                    ? <button onClick={() => setPreviewDoc({ url: getFileUrl(selected.attachment.filename), filename: selected.attachment.originalName })} style={{ background: 'none', border: 'none', padding: 0, color: '#0077b6', fontSize: '13px', fontWeight: 500, cursor: 'pointer', textAlign: 'left' }}>{selected.attachment.originalName}</button>
                    : <span style={{ color: 'var(--text-3)' }}>No file attached</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="card quick-actions-card">
            <div className="card-header"><div className="card-title">Summary</div></div>
            <div className="card-body" style={{ textAlign: 'center', padding: '24px', display: 'grid', gap: '16px' }}>
              {getFileUrl(selected.attachment?.filename)
                ? <button className="btn btn-primary" style={{ display: 'block', width: '100%' }} onClick={() => setPreviewDoc({ url: getFileUrl(selected.attachment.filename), filename: selected.attachment.originalName })}>Preview File</button>
                : <button className="btn btn-secondary" disabled style={{ opacity: 0.45, cursor: 'not-allowed' }}>No File Attached</button>}
              <div><div style={{ fontSize: '28px', fontWeight: 800, color }}>{selected.costType}</div><div style={{ fontSize: '12px', color: 'var(--text-3)' }}>cost type</div></div>
              <div style={{ background: '#f0f9ff', borderRadius: '8px', padding: '12px' }}>
                <div style={{ fontSize: '26px', fontWeight: 700, color: '#0077b6' }}>{selected.amount?.toLocaleString()} <span style={{ fontSize: '14px' }}>{selected.currency || 'RON'}</span></div>
                <div style={{ fontSize: '12px', color: 'var(--text-3)' }}>amount</div>
              </div>
              <div><div style={{ fontSize: '12px', color: 'var(--text-3)' }}>Date</div><div style={{ fontWeight: 600, color: 'var(--text-1)' }}>{formatDate(selected.date)}</div></div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <div className="card-header"><div className="card-title">Quick Actions</div></div>
            <div className="card-body" style={{ display: 'grid', gap: '10px' }}>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowEventForm(true)}>Add Event</button>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowAccidentForm(true)}>Report Accident</button>
              <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowForm(true)}>Add Cost</button>
            </div>
          </div>

          {/* Vehicle Info */}
          <div className="card">
            <div className="card-header"><div className="card-title">Vehicle</div></div>
            <div className="card-body">
              {relatedVehicle ? (
                [['Registration', relatedVehicle.plate], ['Brand / Model', `${relatedVehicle.brand} ${relatedVehicle.model}`], ['Year / Fuel', `${relatedVehicle.year} · ${relatedVehicle.fuel}`], ['Body type', relatedVehicle.type || '—'], ['Company', relatedVehicle.company || '—'], ['Status', relatedVehicle.status || '—']].map(([lbl, val]) => (
                  <div key={lbl} className="info-row"><div className="info-lbl">{lbl}</div><div className="info-val">{val}</div></div>
                ))
              ) : <div style={{ color: 'var(--text-3)', padding: '12px 0', fontSize: '13px' }}>Vehicle info not available.</div>}
            </div>
          </div>

          {/* Events for this vehicle */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Events</div>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowEventForm(true)}>+ Event</button>
            </div>
            <div className="card-body">
              {relatedEvents.length === 0 ? (
                <div style={{ color: 'var(--text-3)', fontSize: '13px', padding: '8px 0' }}>No events for this vehicle.</div>
              ) : relatedEvents.slice(0, 5).map((ev, i) => {
                const st = getStatus(ev);
                return (
                  <div key={ev._id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: i < Math.min(relatedEvents.length, 5) - 1 ? '1px solid #f3f4f6' : 'none' }}>
                    <div><div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-1)' }}>{ev.evt || 'Event'}</div><div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px' }}>{ev.resp || '—'}</div></div>
                    <span style={{ padding: '2px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 600, color: STATUS_COLOR[st], backgroundColor: STATUS_BG[st] }}>{STATUS_LABEL[st]}</span>
                  </div>
                );
              })}
              {relatedEvents.length > 5 && <div style={{ fontSize: '11px', color: 'var(--text-3)', textAlign: 'center', paddingTop: '6px' }}>+{relatedEvents.length - 5} more</div>}
            </div>
          </div>

          {/* Accidents for this vehicle */}
          <div className="card">
            <div className="card-header">
              <div className="card-title">Accidents</div>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAccidentForm(true)}>+ Report</button>
            </div>
            <div className="card-body">
              {relatedAccidents.length === 0 ? (
                <div style={{ color: 'var(--text-3)', fontSize: '13px', padding: '8px 0' }}>No accidents for this vehicle.</div>
              ) : relatedAccidents.slice(0, 5).map((a, i) => (
                <div key={a._id || i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: i < Math.min(relatedAccidents.length, 5) - 1 ? '1px solid #f3f4f6' : 'none' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: DAMAGE_COLORS[a.damageLevel] || '#9ca3af' }}>{DAMAGE_LABELS[a.damageLevel] || 'Unknown'}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '2px' }}>{formatDate(a.date)}{a.location ? ` • ${a.location}` : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '12px', color: CLAIM_COLORS[a.claimStatus] || '#9ca3af', fontWeight: 600 }}>{a.claimStatus?.toUpperCase() || '—'}</div>
                    {a.repairCost && <div style={{ fontSize: '13px', fontWeight: 700, color: '#0077b6' }}>{Number(a.repairCost).toLocaleString()} {a.currency || 'RON'}</div>}
                  </div>
                </div>
              ))}
              {relatedAccidents.length > 5 && <div style={{ fontSize: '11px', color: 'var(--text-3)', textAlign: 'center', paddingTop: '6px' }}>+{relatedAccidents.length - 5} more</div>}
            </div>
          </div>
        </div>

        <CostForm isOpen={showForm} onClose={() => setShowForm(false)} cost={{ plate, vehicleName: selected.vehicleName || '' }} onSave={handleSave} />
        <EventForm isOpen={showEventForm} onClose={() => setShowEventForm(false)} event={{ plate, car: selected.vehicleName || '' }} onSave={handleSaveEvent} />
        <AccidentForm isOpen={showAccidentForm} onClose={() => setShowAccidentForm(false)} accident={null} defaultPlate={plate} defaultVehicleName={selected.vehicleName || ''} onSave={handleSaveAccident} />
        <DocViewer url={previewDoc?.url} filename={previewDoc?.filename} onClose={() => setPreviewDoc(null)} />
      </div>
    );
  }

  // ── LIST VIEW ────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="ph">
        <div>
          <div className="ph-title">My Costs</div>
          <div className="ph-sub">Total: {total.toLocaleString(undefined, { minimumFractionDigits: 2 })} RON</div>
        </div>
        <button className="btn btn-primary btn-sm" onClick={() => setShowForm(true)}>+ Add Cost</button>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
        {Object.entries(byType).map(([type, amt]) => (
          <div key={type} className="stat-card" style={{ borderTop: `3px solid ${TYPE_COLORS[type] || '#9ca3af'}` }}>
            <div className="stat-label">{type.toUpperCase()}</div>
            <div className="stat-value" style={{ fontSize: '18px' }}>{amt.toLocaleString(undefined, { minimumFractionDigits: 0 })}</div>
            <div className="stat-meta">RON</div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginTop: '12px' }}>
        <div className="card-body" style={{ overflowX: 'auto', padding: 0 }}>
          {costs.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-3)' }}>No cost entries yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', minWidth: '760px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                  {['PLATE / VEHICLE', 'DATE', 'TYPE', 'DESCRIPTION', 'RECEIPT REF', 'AMOUNT', 'FILE', ''].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '12px', color: '#9ca3af', fontWeight: 600, fontSize: '11px', letterSpacing: '0.05em' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {costs.map(c => (
                  <tr key={c._id} style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }} onClick={() => setSelected(c)}>
                    <td style={{ padding: '12px' }}><span className="plate">{c.plate}</span>{c.vehicleName && <div style={{ fontSize: '11px', color: 'var(--text-3)', marginTop: '3px' }}>{c.vehicleName}</div>}</td>
                    <td style={{ padding: '12px' }}>{formatDate(c.date)}</td>
                    <td style={{ padding: '12px' }}><span style={{ padding: '3px 8px', borderRadius: '9999px', fontSize: '11px', fontWeight: 700, color: TYPE_COLORS[c.costType] || '#9ca3af', backgroundColor: `${TYPE_COLORS[c.costType] || '#9ca3af'}18` }}>{c.costType}</span></td>
                    <td style={{ padding: '12px', color: 'var(--text-3)' }}>{c.description || '—'}</td>
                    <td style={{ padding: '12px', color: 'var(--text-3)', fontSize: '12px' }}>{c.receiptRef || '—'}</td>
                    <td style={{ padding: '12px', fontWeight: 700 }}>{c.amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })} {c.currency}</td>
                    <td style={{ padding: '12px' }} onClick={e => e.stopPropagation()}>
                      {getFileUrl(c.attachment?.filename)
                        ? <button className="btn btn-ghost btn-xs" style={{ color: '#10b981' }} onClick={e => { e.stopPropagation(); setPreviewDoc({ url: getFileUrl(c.attachment.filename), filename: c.attachment.originalName }); }}>Preview</button>
                        : <span className="btn btn-ghost btn-xs" style={{ color: '#d1d5db', cursor: 'default' }}>Preview</span>}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right' }}><button className="btn btn-ghost btn-xs" onClick={e => { e.stopPropagation(); setSelected(c); }}>View →</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <CostForm isOpen={showForm} onClose={() => setShowForm(false)} cost={null} onSave={handleSave} />
      <DocViewer url={previewDoc?.url} filename={previewDoc?.filename} onClose={() => setPreviewDoc(null)} />
    </div>
  );
};

export default DriverCosts;
